/**
 * ELVOAQ AG – Homepage
 * Real product: Smart item catalog & collection management
 * Light theme · GSAP · i18n · sf-* CSS classes
 */

import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight, faBars, faBrain, faChartLine, faCheck, faCheckCircle,
  faCubes, faEnvelope, faGlobe, faLayerGroup, faLock,
  faSearch, faShieldAlt, faTimes, faUsers, faUserShield, faCookieBite,
  faMapMarkerAlt, faBoxOpen, faStar, faCalendarAlt,
  faFileExport, faFolderOpen, faClone, faRobot, faTag, faBell,
  faChartBar, faWrench, faBuilding, faGraduationCap, faChevronDown,
  faQuestion,
} from '@fortawesome/free-solid-svg-icons';
import { faTwitter, faLinkedinIn, faGithub } from '@fortawesome/free-brands-svg-icons';
import useI18n from '../hooks/useI18n';
import { publicAPI } from '../services/api';
import CloudflareTurnstile from '../components/TurnstileWidget';
import '../styles/HomepageGSAP.css';

gsap.registerPlugin(ScrollTrigger);

type Lang = 'en' | 'de' | 'cs' | 'sk';

// ─── FAQ accordion item ───────────────────────────────────────────────────────
const FAQItem = ({ q, a }: { q: string; a: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`sf-faq-item sf-reveal`}>
      <button className={`sf-faq-item__q${open ? ' sf-faq-item__q--open' : ''}`} onClick={() => setOpen(v => !v)}>
        <span>{q}</span>
        <FontAwesomeIcon icon={faChevronDown} />
      </button>
      {open && <div className="sf-faq-item__a">{a}</div>}
    </div>
  );
};

const Homepage = () => {
  const { language, setLanguage } = useI18n();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [contactSending, setContactSending] = useState(false);
  const [contactStatus, setContactStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const mainRef = useRef<HTMLDivElement>(null);

  const copy = {
    en: {
      navFeatures: 'Features', navAi: 'AI Tools', navPricing: 'Pricing', navContact: 'Contact', signIn: 'Sign In', startFree: 'Start Free',
      heroBadge: 'Free forever plan — no credit card needed',
      heroTitleTop: 'Your entire collection,', heroTitleBottom: 'organized & valued',
      heroSubtitle: 'ELVOAQ is the smart platform to catalog, track value, and manage everything you own — collectibles, equipment, household items, and business assets, all in one place.',
      getStarted: 'Get started free', seeAllFeatures: 'See all features',
      contactTitle: 'Get in touch', contactSub: 'Questions about plans, enterprise pricing, custom integrations, or anything else — we reply within 24 hours.',
      fieldName: 'Your name', fieldEmail: 'Email address', fieldSubject: 'Subject', fieldMessage: 'Your message...',
      sendMessage: 'Send message', sending: 'Sending...',
      contactOk: 'Message sent successfully. Our team will reply soon.',
      contactErr: 'Message could not be sent. Please try again in a moment.',
      legalTitle: 'Legal & Privacy', legalSub: 'ELVOAQ AG is registered in Switzerland. We are transparent about how we handle your data.',
      readPolicy: 'Read full policy', readTerms: 'Read terms of use',
      insightsTitle: 'Platform insights that help you decide',
      insightsSub: 'ELVOAQ turns your item data into practical decisions for insurance, finance, maintenance, and team operations.',
      month: 'Monthly', year: 'Yearly', save: 'Save ~20%',
      sectionFeatures: 'Everything your collection needs',
      sectionFeaturesDesc: 'A complete platform to catalog, value, protect, and share everything you own — with AI doing the heavy lifting.',
      sectionHowItWorks: 'Up and running in minutes',
      sectionHowItWorksDesc: 'Three simple steps from zero to a fully organized, AI-valued inventory.',
      sectionWhoUses: 'Built for everyone',
      sectionWhoUsesDesc: 'Collectors, families, businesses, and professionals — ELVOAQ adapts to how you work.',
      sectionAI: 'AI that works for your collection',
      sectionAIDesc: 'Six intelligent features built into every plan. Powered by ELVOAQ AI — no setup required.',
      sectionPricing: 'Simple, transparent pricing',
      sectionPricingDesc: 'Start free forever. Upgrade only when you need more. Every paid plan includes a 14-day trial and 30-day money-back.',
      sectionSecurity: 'Your data is safe with us',
      sectionSecurityDesc: 'ELVOAQ AG is incorporated in Switzerland. We take security, privacy, and EU data residency seriously.',
      sectionFAQ: 'Frequently asked questions',
      sectionFAQDesc: 'Everything you need to know about ELVOAQ Vault.',
      faqQ1: 'Is ELVOAQ really free to use?',
      faqA1: 'Yes. The Free plan is free forever — no credit card required. You get 30 items, 300 MB storage, photo upload, and core AI features. Paid plans start at €9/mo and unlock more items, storage, AI reports, and team features.',
      faqQ2: 'Can I import my existing inventory data?',
      faqA2: 'Yes. Go to Settings → Import/Export and upload a CSV, JSON, or Excel file. Our visual column mapper helps you match fields. Up to 10,000 items can be imported at once. Larger imports are sent by email when ready.',
      faqQ3: 'How does AI value prediction work?',
      faqA3: 'Our AI analyzes item name, category, condition, and historical data to estimate current fair market value. Estimates show a confidence level (high, medium, low). Values are re-estimated when item details change.',
      faqQ4: 'Is my data private and secure?',
      faqA4: "All data is encrypted at rest (AES-256) and in transit (TLS 1.3). Servers are in EU data centres. We don't sell your data. You can export everything or delete your account permanently at any time.",
      faqQ5: 'What devices does ELVOAQ support?',
      faqA5: 'ELVOAQ Vault is a web app that works on any modern browser — desktop, tablet, and mobile. A native iOS and Android app is on our roadmap for 2026. No installation required.',
      tryHelpCenter: 'Visit Help Center',
      readCookiePolicy: 'Read cookie policy',
      moreQuestions: 'Have more questions?',
      // Stats & Hero
      statsCategories: 'Item categories',
      statsPrediction: 'Value prediction',
      statsLanguages: 'Languages',
      statsUptime: 'Uptime guarantee',
      statsMoneyBack: 'Money-back',
      statsFreeTrial: 'Free trial',
      heroFloatLabel: 'AI Value Estimate',
      heroFloatValue: 'portfolio value',
      trustedByLabel: 'Trusted by collectors, families, and businesses',
      // Pricing Plans
      pricingFreeDesc: 'Get started — catalog up to 30 items with basic AI features.',
      pricingStarterDesc: 'For hobbyists and small collectors getting serious.',
      pricingCollectorDesc: 'For dedicated collectors who need more room and smarter AI tools.',
      pricingProDesc: 'For power users and professionals managing large inventories.',
      pricingFreeF1: 'Up to 30 items', pricingFreeF2: '300 MB storage', pricingFreeF3: '5 photos / item', pricingFreeF4: 'AI categorization', pricingFreeF5: '10 AI messages / mo', pricingFreeF6: 'Basic analytics', pricingFreeF7: '2FA security',
      pricingStarterF1: 'Up to 250 items', pricingStarterF2: '2 GB storage', pricingStarterF3: '10 photos / item', pricingStarterF4: 'AI categorization', pricingStarterF5: '5 AI reports / mo', pricingStarterF6: '50 AI messages / mo', pricingStarterF7: 'Data export (CSV/JSON)',
      pricingCollectorF1: 'Up to 1,000 items', pricingCollectorF2: '10 GB storage', pricingCollectorF3: '20 photos / item', pricingCollectorF4: 'AI value prediction', pricingCollectorF5: 'Duplicate detection', pricingCollectorF6: '100 AI messages / mo', pricingCollectorF7: '30-day audit logs',
      pricingProF1: 'Up to 10,000 items', pricingProF2: '50 GB storage', pricingProF3: '40 photos / item', pricingProF4: 'Unlimited AI reports', pricingProF5: '500 AI messages / mo', pricingProF6: 'Advanced analytics', pricingProF7: 'Priority support',
      // Features Section
      itemLibraryTitle: 'Item Library',
      itemLibraryDesc: 'Catalog every item you own with photos, values, conditions, purchase details, tags, and custom notes. Find anything instantly with smart filters.',
      aiValuePredictionTitle: 'AI Value Prediction',
      aiValuePredictionDesc: 'Get AI-powered market value estimates for your items. Know what your collection is really worth with high, medium, or low confidence indicators.',
      duplicateDetectionTitle: 'Duplicate Detection',
      duplicateDetectionDesc: 'Upload a photo and our AI instantly scans your entire collection for matching or similar items — saving you from accidental double purchases.',
      collectionsFoldersTitle: 'Collections & Folders',
      collectionsFoldersDesc: 'Group items into named collections — a travel kit, home office, insurance bundle. Hidden password-protected folders keep private items secure.',
      analyticsDashboardTitle: 'Analytics Dashboard',
      analyticsDashboardDesc: 'Track portfolio value over time, view category breakdowns, condition distribution, acquisition cost history, and build custom charts.',
      teamsWorkspacesTitle: 'Teams & Workspaces',
      teamsWorkspacesDesc: 'Invite team members, assign roles, collaborate in shared workspaces. Full audit trail, role-based access control, and live activity feeds.',
      // More Powerful Features
      morePowerfulTitle: 'More powerful features',
      morePowerfulSubtitle: 'Built for serious collectors and pros',
      calendarRemindersTitle: 'Calendar & Reminders',
      calendarRemindersDesc: 'Schedule warranty expirations, insurance renewals, maintenance dates, and custom events — all linked to your inventory items.',
      assetAccountingTitle: 'Asset Accounting',
      assetAccountingDesc: 'Track depreciation using multiple methods (straight-line, declining balance). Generate asset registers and impairment reports.',
      importExportTitle: 'Import & Export',
      importExportDesc: 'Export your full catalog as CSV or JSON at any time. Import from existing spreadsheets. No lock-in, ever.',
      auditLogTitle: 'Audit Log',
      auditLogDesc: 'Every change is tracked: who changed what, when, from where. Revert any change with one click on Business and above.',
      // Products section
      productsTitle: 'Our Products',
      productsSubtitle: 'Smart tools to manage what matters.',
      vaultTitle: 'ELVOAQ Vault',
      vaultDesc: 'The smart inventory platform you are looking at right now. Catalog, value, and manage everything you own with AI-powered tools.',
      vaultBadge: 'Available now',
      timeCalcTitle: 'Time Calculator',
      timeCalcDesc: 'A precision time and date calculator for professionals. Convert, compare, and calculate durations across time zones with ease.',
      timeCalcBadge: 'Coming soon',
    },
    de: {
      navFeatures: 'Funktionen', navAi: 'KI-Tools', navPricing: 'Preise', navContact: 'Kontakt', signIn: 'Anmelden', startFree: 'Kostenlos starten',
      heroBadge: 'Dauerhaft kostenloser Plan — keine Kreditkarte nötig',
      heroTitleTop: 'Deine gesamte Sammlung,', heroTitleBottom: 'organisiert & bewertet',
      heroSubtitle: 'ELVOAQ ist die smarte Plattform zum Katalogisieren, Bewerten und Verwalten deiner Gegenstände — Sammlerstücke, Ausrüstung, Haushalts- und Unternehmenswerte an einem Ort.',
      getStarted: 'Kostenlos starten', seeAllFeatures: 'Alle Funktionen',
      contactTitle: 'Kontakt aufnehmen', contactSub: 'Fragen zu Tarifen, Enterprise-Preisen, Integrationen oder etwas anderem? Wir antworten innerhalb von 24 Stunden.',
      fieldName: 'Dein Name', fieldEmail: 'E-Mail-Adresse', fieldSubject: 'Betreff', fieldMessage: 'Deine Nachricht...',
      sendMessage: 'Nachricht senden', sending: 'Wird gesendet...',
      contactOk: 'Nachricht erfolgreich gesendet. Unser Team meldet sich bald.',
      contactErr: 'Nachricht konnte nicht gesendet werden. Bitte versuche es erneut.',
      legalTitle: 'Rechtliches & Datenschutz', legalSub: 'ELVOAQ AG ist in der Schweiz registriert. Wir gehen transparent mit deinen Daten um.',
      readPolicy: 'Vollständige Richtlinie lesen', readTerms: 'Nutzungsbedingungen lesen',
      insightsTitle: 'Plattform-Einblicke für bessere Entscheidungen',
      insightsSub: 'ELVOAQ wandelt Inventardaten in konkrete Entscheidungen für Versicherung, Finanzen, Wartung und Teamarbeit um.',
      month: 'Monatlich', year: 'Jährlich', save: 'Spare ~20%',
      sectionFeatures: 'Alles, was deine Sammlung braucht',
      sectionFeaturesDesc: 'Eine vollständige Plattform zum Katalogisieren, Bewerten, Schützen und Teilen all deiner Gegenstände — KI erledigt die schwere Arbeit.',
      sectionHowItWorks: 'In Minuten einsatzbereit',
      sectionHowItWorksDesc: 'Drei einfache Schritte von null zu einem vollständig organisierten, KI-bewerteten Inventar.',
      sectionWhoUses: 'Für alle gebaut',
      sectionWhoUsesDesc: 'Sammler, Familien, Unternehmen und Profis — ELVOAQ passt sich deiner Arbeitsweise an.',
      sectionAI: 'KI, die für deine Sammlung arbeitet',
      sectionAIDesc: 'Sechs intelligente Funktionen in jedem Plan enthalten. Powered by ELVOAQ AI — kein Setup erforderlich.',
      sectionPricing: 'Einfache, transparente Preise',
      sectionPricingDesc: 'Für immer kostenlos starten. Der Upgrade nur, wenn du mehr benötigst. Jeder bezahlte Plan beinhaltet 14 Tage Testphase und 30 Tage Geld-zurück.',
      sectionSecurity: 'Deine Daten sind bei uns sicher',
      sectionSecurityDesc: 'ELVOAQ AG ist in der Schweiz eingetragen. Sicherheit, Datenschutz und EU-Datenspeicherung liegen uns am Herzen.',
      sectionFAQ: 'Häufig gestellte Fragen',
      sectionFAQDesc: 'Alles, was du über ELVOAQ Vault wissen musst.',
      faqQ1: 'Ist ELVOAQ wirklich kostenlos nutzbar?',
      faqA1: 'Ja. Der Free-Plan ist dauerhaft kostenlos — keine Kreditkarte erforderlich. Du erhältst 30 Artikel, 300 MB Speicher und Kern-KI-Funktionen. Bezahlte Pläne beginnen bei €9/Monat.',
      faqQ2: 'Kann ich meine vorhandenen Daten importieren?',
      faqA2: 'Ja. Gehe zu Einstellungen → Import/Export und lade eine CSV-, JSON- oder Excel-Datei hoch. Unser visueller Spalten-Mapper hilft dir bei der Feldzuordnung. Bis zu 10.000 Artikel können auf einmal importiert werden.',
      faqQ3: 'Wie funktioniert die KI-Wertvorhersage?',
      faqA3: 'Unsere KI analysiert Name, Kategorie, Zustand und historische Daten, um den aktuellen Marktwert zu schätzen. Schätzungen zeigen ein Konfidenzniveau (hoch, mittel, niedrig) an.',
      faqQ4: 'Sind meine Daten privat und sicher?',
      faqA4: 'Alle Daten sind im Ruhezustand (AES-256) und bei der Übertragung (TLS 1.3) verschlüsselt. Server befinden sich in EU-Rechenzentren. Wir verkaufen deine Daten nicht.',
      faqQ5: 'Welche Geräte werden unterstützt?',
      faqA5: 'ELVOAQ Vault ist eine Web-App, die in jedem modernen Browser funktioniert — Desktop, Tablet und Mobilgerät. Eine native iOS- und Android-App ist für 2026 geplant.',
      tryHelpCenter: 'Hilfezentrum besuchen',
      readCookiePolicy: 'Cookie-Richtlinie lesen',
      moreQuestions: 'Weitere Fragen?',
      // Stats & Hero
      statsCategories: 'Elementkategorien',
      statsPrediction: 'Wertvorhersage',
      statsLanguages: 'Sprachen',
      statsUptime: 'Verfügbarkeitsgarantie',
      statsMoneyBack: 'Geld-zurück',
      statsFreeTrial: 'Kostenlose Testversion',
      heroFloatLabel: 'KI-Wertschätzung',
      heroFloatValue: 'Portfoliowert',
      trustedByLabel: 'Vertraut von Sammlern, Familien und Unternehmen',
      // Pricing Plans
      pricingFreeDesc: 'Kostenlos starten — katalogisiere bis zu 30 Elemente mit grundlegenden KI-Funktionen.',
      pricingStarterDesc: 'Für Enthusiasten und kleine Sammler, die es ernst meinen.',
      pricingCollectorDesc: 'Für engagierte Sammler, die mehr Platz und intelligentere KI-Tools benötigen.',
      pricingProDesc: 'Für Poweruser und Profis, die große Bestände verwalten.',
      pricingFreeF1: 'Bis zu 30 Elemente', pricingFreeF2: '300 MB Speicher', pricingFreeF3: '5 Fotos / Element', pricingFreeF4: 'KI-Kategorisierung', pricingFreeF5: '10 KI-Nachrichten / Monat', pricingFreeF6: 'Grundlegende Analytik', pricingFreeF7: '2FA-Sicherheit',
      pricingStarterF1: 'Bis zu 250 Elemente', pricingStarterF2: '2 GB Speicher', pricingStarterF3: '10 Fotos / Element', pricingStarterF4: 'KI-Kategorisierung', pricingStarterF5: '5 KI-Berichte / Monat', pricingStarterF6: '50 KI-Nachrichten / Monat', pricingStarterF7: 'Datenexport (CSV/JSON)',
      pricingCollectorF1: 'Bis zu 1.000 Elemente', pricingCollectorF2: '10 GB Speicher', pricingCollectorF3: '20 Fotos / Element', pricingCollectorF4: 'KI-Wertvorhersage', pricingCollectorF5: 'Duplikaterkennung', pricingCollectorF6: '100 KI-Nachrichten / Monat', pricingCollectorF7: '30-Tage-Audit-Protokolle',
      pricingProF1: 'Bis zu 10.000 Elemente', pricingProF2: '50 GB Speicher', pricingProF3: '40 Fotos / Element', pricingProF4: 'Unbegrenzte KI-Berichte', pricingProF5: '500 KI-Nachrichten / Monat', pricingProF6: 'Erweiterte Analytik', pricingProF7: 'Prioritätssupport',
      // Features Section
      itemLibraryTitle: 'Elementverwaltung',
      itemLibraryDesc: 'Katalogisiere jedes Element mit Fotos, Werten, Bedingungen, Kaufdetails, Tags und benutzerdefinierten Notizen. Finde alles sofort mit intelligenten Filtern.',
      aiValuePredictionTitle: 'KI-Wertvorhersage',
      aiValuePredictionDesc: 'Erhalte KI-gestützte Marktwertschätzungen für deine Gegenstände. Erfahre, was deine Sammlung wirklich wert ist, mit hohem, mittlerem oder niedrigem Vertrauen.',
      duplicateDetectionTitle: 'Duplikaterkennung',
      duplicateDetectionDesc: 'Lade ein Foto hoch und unsere KI scannt sofort deine gesamte Sammlung nach übereinstimmenden Elementen — erspart dir versehentliche Doppelkäufe.',
      collectionsFoldersTitle: 'Sammlungen & Ordner',
      collectionsFoldersDesc: 'Gruppiere Gegenstände in benannte Sammlungen — ein Reisekoffer, ein Heimbüro, eine Versicherungsbündel. Versteckte passwortgeschützte Ordner halten private Gegenstände sicher.',
      analyticsDashboardTitle: 'Analyse-Dashboard',
      analyticsDashboardDesc: 'Verfolge den Portfoliowert über die Zeit, siehe Kategorieaufschlüsselungen, Zustandsverteilung, Kaufkostenhistorie und erstelle benutzerdefinierte Diagramme.',
      teamsWorkspacesTitle: 'Teams & Arbeitsbereiche',
      teamsWorkspacesDesc: 'Laden Sie Teammitglieder ein, weisen Sie Rollen zu, arbeiten Sie in gemeinsamen Arbeitsbereichen zusammen. Vollständiges Audit-Trail, rollenbasierte Zugriffskontrolle und Live-Aktivitätsfeeds.',
      // More Powerful Features
      morePowerfulTitle: 'Noch leistungsfähigere Funktionen',
      morePowerfulSubtitle: 'Gebaut für ernsthafte Sammler und Profis',
      calendarRemindersTitle: 'Kalender & Erinnerungen',
      calendarRemindersDesc: 'Plant Garantieverfalls-Daten, Versicherungserneuerungen, Wartungstermine und benutzerdefinierte Ereignisse — alles mit deinen Inventargegenständen verknüpft.',
      assetAccountingTitle: 'Vermögensrechnung',
      assetAccountingDesc: 'Verfolge Abschreibungen mit mehreren Methoden (linear, degressiv). Generiere Vermögensregister und Wertminderungsberichte.',
      importExportTitle: 'Importieren & Exportieren',
      importExportDesc: 'Exportiere deinen gesamten Katalog jederzeit als CSV oder JSON. Importiere aus vorhandenen Tabellenkalkulationen. Keine Abhängigkeit, überhaupt.',
      auditLogTitle: 'Audit-Log',
      auditLogDesc: 'Jede Änderung wird verfolgt: wer, was, wann, von wo. Revertiere jede Änderung mit einem Klick bei Business und höher.',
      // Products section
      productsTitle: 'Unsere Produkte',
      productsSubtitle: 'Smarte Tools für das, was zählt.',
      vaultTitle: 'ELVOAQ Vault',
      vaultDesc: 'Die intelligente Inventarplattform, die du gerade siehst. Katalogisiere, bewerte und verwalte alles mit KI-gestützten Tools.',
      vaultBadge: 'Jetzt verfügbar',
      timeCalcTitle: 'Zeitrechner',
      timeCalcDesc: 'Ein präziser Zeit- und Datumsrechner für Profis. Konvertiere, vergleiche und berechne Zeitspannen über Zeitzonen hinweg.',
      timeCalcBadge: 'Demnächst verfügbar',
    },
    cs: {
      navFeatures: 'Funkce', navAi: 'AI nástroje', navPricing: 'Ceník', navContact: 'Kontakt', signIn: 'Přihlásit se', startFree: 'Začít zdarma',
      heroBadge: 'Plán zdarma napořád — bez kreditní karty',
      heroTitleTop: 'Celá vaše sbírka,', heroTitleBottom: 'organizovaná a oceněná',
      heroSubtitle: 'ELVOAQ je chytrá platforma pro katalogizaci, sledování hodnoty a správu všeho, co vlastníte — sběratelské předměty, vybavení, domácí i firemní majetek na jednom místě.',
      getStarted: 'Začít zdarma', seeAllFeatures: 'Zobrazit všechny funkce',
      contactTitle: 'Kontaktujte nás', contactSub: 'Dotazy k plánům, enterprise cenám, integracím nebo čemukoli dalšímu — odpovíme do 24 hodin.',
      fieldName: 'Vaše jméno', fieldEmail: 'E-mail', fieldSubject: 'Předmět', fieldMessage: 'Vaše zpráva...',
      sendMessage: 'Odeslat zprávu', sending: 'Odesílání...',
      contactOk: 'Zpráva byla úspěšně odeslána. Brzy se ozveme.',
      contactErr: 'Zprávu se nepodařilo odeslat. Zkuste to prosím znovu.',
      legalTitle: 'Právní informace a soukromí', legalSub: 'ELVOAQ AG je registrována ve Švýcarsku. Transparentně vysvětlujeme, jak pracujeme s daty.',
      readPolicy: 'Přečíst zásady ochrany soukromí', readTerms: 'Přečíst podmínky používání',
      insightsTitle: 'Přehledy platformy pro lepší rozhodování',
      insightsSub: 'ELVOAQ převádí data o položkách na praktická rozhodnutí pro pojištění, finance, údržbu i týmovou práci.',
      month: 'Měsíčně', year: 'Ročně', save: 'Ušetřete ~20%',
      sectionFeatures: 'Vše, co vaše sbírka potřebuje',
      sectionFeaturesDesc: 'Kompletní platforma pro katalogizaci, ocenění, ochranu a sdílení všeho, co vlastníte — s AI, která dělá těžkou práci.',
      sectionHowItWorks: 'Spuštění za minuty',
      sectionHowItWorksDesc: 'Tři jednoduché kroky od nuly k plně organizovanému inventáři s AI oceněním.',
      sectionWhoUses: 'Postaveno pro každého',
      sectionWhoUsesDesc: 'Sběratelé, rodiny, firmy i profesionálové — ELVOAQ se přizpůsobí vašemu způsobu práce.',
      sectionAI: 'AI, která pracuje pro vaši sbírku',
      sectionAIDesc: 'Šest inteligentních funkcí v každém plánu. Poháněno ELVOAQ AI — bez nutnosti nastavení.',
      sectionPricing: 'Jednoduché, transparentní ceny',
      sectionPricingDesc: 'Začněte zdarma navždy. Upgradujte jen tehdy, kdy potřebujete více. Každý placený plán zahrnuje 14denní zkušební verzi a 30denní záruku vrácení peněz.',
      sectionSecurity: 'Vaše data jsou u nás v bezpečí',
      sectionSecurityDesc: 'ELVOAQ AG je registrována ve Švýcarsku. Bezpečnost, soukromí a umístění dat v EU bereme vážně.',
      sectionFAQ: 'Nejčastěji kladené otázky',
      sectionFAQDesc: 'Vše, co potřebujete vědět o ELVOAQ Vault.',
      faqQ1: 'Je ELVOAQ opravdu zdarma?',
      faqA1: 'Ano. Bezplatný plán je navždy zdarma — bez kreditní karty. Získáte 30 položek, 300 MB úložiště a základní funkce AI. Placené plány začínají od €9/měs.',
      faqQ2: 'Mohu importovat stávající data inventáře?',
      faqA2: 'Ano. Přejděte do Nastavení → Import/Export a nahrajte soubor CSV, JSON nebo Excel. Náš vizuální mapovač sloupců vám pomůže přiřadit pole. Najednou lze importovat až 10 000 položek.',
      faqQ3: 'Jak funguje predikce hodnoty pomocí AI?',
      faqA3: 'Naše AI analyzuje název položky, kategorii, stav a historická data, aby odhadla aktuální tržní hodnotu. Odhady zobrazují úroveň spolehlivosti (vysoká, střední, nízká).',
      faqQ4: 'Jsou moje data soukromá a bezpečná?',
      faqA4: 'Všechna data jsou šifrována v klidu (AES-256) i při přenosu (TLS 1.3). Servery se nacházejí v datových centrech v EU. Vaše data neprodáváme.',
      faqQ5: 'Která zařízení jsou podporována?',
      faqA5: 'ELVOAQ Vault je webová aplikace, která funguje v každém moderním prohlížeči — na počítači, tabletu i mobilu. Nativní aplikace pro iOS a Android je v plánu na rok 2026.',
      tryHelpCenter: 'Navštívit centrum nápovědy',
      readCookiePolicy: 'Přečíst zásady cookies',
      moreQuestions: 'Máte další otázky?',
      // Stats & Hero
      statsCategories: 'Kategorie položek',
      statsPrediction: 'Predikce hodnoty',
      statsLanguages: 'Jazyky',
      statsUptime: 'Záruka dostupnosti',
      statsMoneyBack: 'Vrácení peněz',
      statsFreeTrial: 'Bezplatné vyzkoušení',
      heroFloatLabel: 'Odhad hodnoty AI',
      heroFloatValue: 'hodnota portfolia',
      trustedByLabel: 'Důvěřuje sbírateľům, rodinám a podnikům',
      // Pricing Plans
      pricingFreeDesc: 'Začněte - katalogizujte až 30 předmětů se základními funkcemi AI.',
      pricingStarterDesc: 'Pro nadšence a malé sběratele, kterým jde o věc.',
      pricingCollectorDesc: 'Pro vážné sběratele, kteří potřebují více místa a chytřejší nástroje AI.',
      pricingProDesc: 'Pro pokročilé uživatele a profesionály spravující velké inventáře.',
      pricingFreeF1: 'Až 30 předmětů', pricingFreeF2: '300 MB úložiště', pricingFreeF3: '5 fotek / předmět', pricingFreeF4: 'Kategorizace AI', pricingFreeF5: '10 zpráv AI / měsíc', pricingFreeF6: 'Základní analýza', pricingFreeF7: 'Zabezpečení 2FA',
      pricingStarterF1: 'Až 250 předmětů', pricingStarterF2: '2 GB úložiště', pricingStarterF3: '10 fotek / předmět', pricingStarterF4: 'Kategorizace AI', pricingStarterF5: '5 zpráv AI / měsíc', pricingStarterF6: '50 zpráv AI / měsíc', pricingStarterF7: 'Export dat (CSV/JSON)',
      pricingCollectorF1: 'Až 1 000 předmětů', pricingCollectorF2: '10 GB úložiště', pricingCollectorF3: '20 fotek / předmět', pricingCollectorF4: 'Predikce hodnoty AI', pricingCollectorF5: 'Detekce duplikátů', pricingCollectorF6: '100 zpráv AI / měsíc', pricingCollectorF7: '30denní audit logy',
      pricingProF1: 'Až 10 000 předmětů', pricingProF2: '50 GB úložiště', pricingProF3: '40 fotek / předmět', pricingProF4: 'Neomezené zprávy AI', pricingProF5: '500 zpráv AI / měsíc', pricingProF6: 'Pokročilá analýza', pricingProF7: 'Prioritní podpora',
      // Features Section
      itemLibraryTitle: 'Knihovna položek',
      itemLibraryDesc: 'Katalogizujte každou položku s fotkami, hodnotami, podmínkami, nákupními detaily, značkami a vlastními poznámkami. Najděte cokoli okamžitě s chytrými filtry.',
      aiValuePredictionTitle: 'Predikce hodnoty AI',
      aiValuePredictionDesc: 'Získejte odhady tržní hodnoty poháněné AI pro vaše položky. Vím, co je vaše sbírka opravdu wert s vysokou, střední nebo nízkou jistotou.',
      duplicateDetectionTitle: 'Detekce duplikátů',
      duplicateDetectionDesc: 'Nahrajte fotografii a naše AI okamžitě prohledá vaši celou sbírku na odpovídající položky — šetří vás před náhodným nákupem duplikátů.',
      collectionsFoldersTitle: 'Sbírky & Složky',
      collectionsFoldersDesc: 'Seskupujte položky do pojmenovaných sbírek — cestovní kufr, kanceláře, pojišťovací balíček. Skryté heslem chráněné složky udržují soukromé předměty v bezpečí.',
      analyticsDashboardTitle: 'Analytický panel',
      analyticsDashboardDesc: 'Sledujte hodnotu portfolia v čase, zobrazte rozpisy kategorií, distribuci podmínek, historii nákupních nákladů a vytvářejte vlastní grafy.',
      teamsWorkspacesTitle: 'Týmy & Pracovní prostory',
      teamsWorkspacesDesc: 'Pozvěte členy týmu, přiřaďte role, spolupracujte ve sdílených pracovních prostorech. Úplné kontroly auditů, přístup na základě rolí a aktivní kanály.',
      // More Powerful Features
      morePowerfulTitle: 'Více výkonných funkcí',
      morePowerfulSubtitle: 'Vytvořeno pro vážné sběratele a profesionály',
      calendarRemindersTitle: 'Kalendář & Připomenutí',
      calendarRemindersDesc: 'Naplánujte vypršení záruk, obnovení pojistky, data údržby a vlastní eventos — vše propojeno s vašimi položkami',
      assetAccountingTitle: 'Účet majetku',
      assetAccountingDesc: 'Sledujte odpisy pomocí více metod (lineární, klesající). Generujte registry aktiv a zprávy o znehodnocení.',
      importExportTitle: 'Import & Export',
      importExportDesc: 'Exportujte svůi úplný katalog kdykoli jako CSV nebo JSON. Importujte z existujících tabulek. Bez vazby, nikdy.',
      auditLogTitle: 'Audit log',
      auditLogDesc: 'Každá změna se sleduje: kdo, co, kdy, odkud. Vraťte jakoukoli změnu jedním klikem na Obchod a vyšší.',
      // Products section
      productsTitle: 'Naše produkty',
      productsSubtitle: 'Chytré nástroje pro správu toho, na čem záleží.',
      vaultTitle: 'ELVOAQ Vault',
      vaultDesc: 'Inteligentní inventární platforma, kterou právě vidíte. Katalogizujte, oceňte a spravujte vše s nástroji poháněnými AI.',
      vaultBadge: 'K dispozici nyní',
      timeCalcTitle: 'Kalkulačka času',
      timeCalcDesc: 'Přesná kalkulačka času a dat pro profesionály. Převádějte, porovnávejte a počítejte časové úseky napříč časovými pásmy.',
      timeCalcBadge: 'Již brzy',
    },
    sk: {
      navFeatures: 'Funkcie', navAi: 'AI nástroje', navPricing: 'Cenník', navContact: 'Kontakt', signIn: 'Prihlásiť sa', startFree: 'Začať zdarma',
      heroBadge: 'Bezplatný plán navždy — bez kreditnej karty',
      heroTitleTop: 'Celá vaša kolekcia,', heroTitleBottom: 'organizovaná a ocenená',
      heroSubtitle: 'ELVOAQ je inteligentná platforma na katalogizáciu, sledovanie hodnoty a správu všetkého, čo vlastníte — zberateľské predmety, vybavenie, domácnosti aj firemné aktíva na jednom mieste.',
      getStarted: 'Začať zdarma', seeAllFeatures: 'Zobraziť všetky funkcie',
      contactTitle: 'Kontaktujte nás', contactSub: 'Otázky k plánom, enterprise cenám, integráciám alebo čomukoľvek inému — odpovieme do 24 hodín.',
      fieldName: 'Vaše meno', fieldEmail: 'E-mail', fieldSubject: 'Predmet', fieldMessage: 'Vaša správa...',
      sendMessage: 'Odoslať správu', sending: 'Odosielanie...',
      contactOk: 'Správa bola úspešne odoslaná. Náš tím sa čoskoro ozve.',
      contactErr: 'Správu sa nepodarilo odoslať. Skúste to znova.',
      legalTitle: 'Právne informácie a súkromie', legalSub: 'ELVOAQ AG je registrovaná vo Švajčiarsku. Transparentne vysvetľujeme, ako pracujeme s údajmi.',
      readPolicy: 'Prečítať zásady ochrany súkromia', readTerms: 'Prečítať podmienky používania',
      insightsTitle: 'Prehľady platformy pre lepšie rozhodnutia',
      insightsSub: 'ELVOAQ premieňa dáta o položkách na praktické rozhodnutia pre poistenie, financie, údržbu a tímovú spoluprácu.',
      month: 'Mesačne', year: 'Ročne', save: 'Ušetrite ~20%',
      sectionFeatures: 'Všetko, čo vaša zbierka potrebuje',
      sectionFeaturesDesc: 'Kompletná platforma na katalogizáciu, ocenenie, ochranu a zdieľanie všetkého, čo vlastníte — s AI, ktorá robí ťažkú prácu.',
      sectionHowItWorks: 'Spustenie za minúty',
      sectionHowItWorksDesc: 'Tri jednoduché kroky od nuly k plne organizovanému inventáru s AI ocenením.',
      sectionWhoUses: 'Vytvorené pre každého',
      sectionWhoUsesDesc: 'Zberatelia, rodiny, firmy aj profesionáli — ELVOAQ sa prispôsobí vášmu spôsobu práce.',
      sectionAI: 'AI, ktorá pracuje pre vašu zbierku',
      sectionAIDesc: 'Šesť inteligentných funkcií v každom pláne. Poháňané ELVOAQ AI — bez potreby nastavenia.',
      sectionPricing: 'Jednoduché, transparentné ceny',
      sectionPricingDesc: 'Začnite bezplatne navždy. Inovujte len vtedy, keď potrebujete viac. Každý platený plán zahŕňa 14-dňovú skúšobodnú verziu a 30-dňovú záruku vrátenia peňazí.',
      sectionSecurity: 'Vaše dáta sú u nás v bezpečí',
      sectionSecurityDesc: 'ELVOAQ AG je registrovaná vo Švajčiarsku. Bezpečnosť, súkromie a umiestnenie dát v EÚ berieme vážne.',
      sectionFAQ: 'Často kladené otázky',
      sectionFAQDesc: 'Všetko, čo potrebujete vedieť o ELVOAQ Vault.',
      faqQ1: 'Je ELVOAQ naozaj bezplatný?',
      faqA1: 'Áno. Bezplatný plán je navždy zdarma — bez kreditnej karty. Získate 30 položiek, 300 MB úložiska a základné funkcie AI. Platené plány začínajú od €9/mes.',
      faqQ2: 'Môžem importovať existujúce dáta inventára?',
      faqA2: 'Áno. Prejdite do Nastavenia → Import/Export a nahrajte súbor CSV, JSON alebo Excel. Vizuálny mapovač stĺpcov vám pomôže priradiť polia. Naraz možno importovať až 10 000 položiek.',
      faqQ3: 'Ako funguje predikcia hodnoty pomocou AI?',
      faqA3: 'Naša AI analyzuje názov položky, kategóriu, stav a historické dáta, aby odhadla aktuálnu trhovú hodnotu. Odhady zobrazujú úroveň spoľahlivosti (vysoká, stredná, nízka).',
      faqQ4: 'Sú moje dáta súkromné a bezpečné?',
      faqA4: 'Všetky dáta sú šifrované v pokoji (AES-256) aj pri prenose (TLS 1.3). Servery sa nachádzajú v dátových centrách v EÚ. Vaše dáta nepredávame.',
      faqQ5: 'Ktoré zariadenia sú podporované?',
      faqA5: 'ELVOAQ Vault je webová aplikácia, ktorá funguje v každom modernom prehliadači — na počítači, tablete aj mobile. Natívna aplikácia pre iOS a Android je plánovaná na rok 2026.',
      tryHelpCenter: 'Navštíviť centrum pomoci',
      readCookiePolicy: 'Prečítať zásady cookies',
      moreQuestions: 'Máte ďalšie otázky?',
      // Stats & Hero
      statsCategories: 'Kategórie položiek',
      statsPrediction: 'Predikcia hodnoty',
      statsLanguages: 'Jazyky',
      statsUptime: 'Záruka dostupnosti',
      statsMoneyBack: 'Vrátenie peňazí',
      statsFreeTrial: 'Bezplatná skúšobná verzia',
      heroFloatLabel: 'Odhad hodnoty AI',
      heroFloatValue: 'hodnota portfólia',
      trustedByLabel: 'Dôverujú zberatelia, rodiny a podniky',
      // Pricing Plans
      pricingFreeDesc: 'Začnite - katalogizujte až 30 položiek so základnými funkciami AI.',
      pricingStarterDesc: 'Pre nadšencov a malých zberateľov, ktorí to myslia vážne.',
      pricingCollectorDesc: 'Pre oddaných zberateľov, ktorí potrebujú viac miesta a chytrejšie nástroje AI.',
      pricingProDesc: 'Pre pokročilých používateľov a profesionálov spravujúcich veľké inventáre.',
      pricingFreeF1: 'Až 30 položiek', pricingFreeF2: '300 MB úložného', pricingFreeF3: '5 fotografií / položku', pricingFreeF4: 'Kategorizácia AI', pricingFreeF5: '10 správ AI / mesiac', pricingFreeF6: 'Základná analýza', pricingFreeF7: '2FA bezpečnosť',
      pricingStarterF1: 'Až 250 položiek', pricingStarterF2: '2 GB úložného', pricingStarterF3: '10 fotografií / položku', pricingStarterF4: 'Kategorizácia AI', pricingStarterF5: '5 správ AI / mesiac', pricingStarterF6: '50 správ AI / mesiac', pricingStarterF7: 'Export údajov (CSV/JSON)',
      pricingCollectorF1: 'Až 1 000 položiek', pricingCollectorF2: '10 GB úložného', pricingCollectorF3: '20 fotografií / položku', pricingCollectorF4: 'Predikcia hodnoty AI', pricingCollectorF5: 'Detekcia duplikátov', pricingCollectorF6: '100 správ AI / mesiac', pricingCollectorF7: '30-deň audit protokolov',
      pricingProF1: 'Až 10 000 položiek', pricingProF2: '50 GB úložného', pricingProF3: '40 fotografií / položku', pricingProF4: 'Neobmedzené správy AI', pricingProF5: '500 správ AI / mesiac', pricingProF6: 'Pokročilá analýza', pricingProF7: 'Prioritná podpora',
      // Features Section
      itemLibraryTitle: 'Knižnica položiek',
      itemLibraryDesc: 'Katalogizujte každú položku s fotkami, hodnotami, podmienkami, nákupnými detailmi, značkami a vlastnými poznámkami. Nájdite čokoľvek okamžite s inteligentnými filtrami.',
      aiValuePredictionTitle: 'Predikcia hodnoty AI',
      aiValuePredictionDesc: 'Získajte odhady tržnej hodnoty poháňané AI pre vaše položky. Viete, čo je vaša zbierka opravdu wert s vysokou, strednou alebo nízkou istotou.',
      duplicateDetectionTitle: 'Detekcia duplikátov',
      duplicateDetectionDesc: 'Nahrajte fotografiu a naša AI okamžite prehľadá vašu celú zbierku na zhodujúce sa položky — šetří vás pred náhodným nákupom duplikátov.',
      collectionsFoldersTitle: 'Zbierky & Zložky',
      collectionsFoldersDesc: 'Zoskupujte položky do pomenovaných zbierok — cestovný kufor, kancelária, poisťovací balík. Skryté heslovane chránené zložky udržiavajú súkromné položky v bezpečí.',
      analyticsDashboardTitle: 'Analytický panel',
      analyticsDashboardDesc: 'Sledujte hodnotu portfólia v čase, zobrazte rozpisy kategórií, distribúciu podmienok, históriu nákupných nákladov a vytvárajte vlastné grafy.',
      teamsWorkspacesTitle: 'Tímy & Pracovné priestory',
      teamsWorkspacesDesc: 'Pozvite členov tímu, prideľte role, spolupracujte v zdieľaných pracovných priestoroch. Úplné audity, prístup na základe rolí a živé kanály aktivity.',
      // More Powerful Features
      morePowerfulTitle: 'Ešte výkonnejšie funkcie',
      morePowerfulSubtitle: 'Vytvorené pre vážnych zberateľov a profesionálov',
      calendarRemindersTitle: 'Kalendár & Pripomenutia',
      calendarRemindersDesc: 'Naplánujte plavnosť záruk, obnovenie poisťovníka, data údržby a vlastné udalosti — všetko prepojené s vašimi položkami.',
      assetAccountingTitle: 'Účtovný majetok',
      assetAccountingDesc: 'Sledujte odpisy pomocou viacerých metód (lineárne, znižujúce sa). Generujte registry aktív a správy o znehodnotení.',
      importExportTitle: 'Import & Export',
      importExportDesc: 'Exportujte svoj úplný katalóg kedykoľvek ako CSV alebo JSON. Importujte z existujúcich tabuliek. Bez viazanosti, nikdy.',
      auditLogTitle: 'Audit log',
      auditLogDesc: 'Každá zmena sa sleduje: kto, čo, kedy, odkiaľ. Vráťte akúkoľvek zmenu jedným kliknutím na Obchod a vyšší.',
      // Products section
      productsTitle: 'Naše produkty',
      productsSubtitle: 'Inteligentné nástroje na správu toho, na čom záleží.',
      vaultTitle: 'ELVOAQ Vault',
      vaultDesc: 'Inteligentná inventárna platforma, ktorú práve vidíte. Katalogizujte, oceňte a spravujte všetko s nástrojmi poháňanými AI.',
      vaultBadge: 'K dispozícii teraz',
      timeCalcTitle: 'Kalkulačka času',
      timeCalcDesc: 'Presná kalkulačka času a dátumov pre profesionálov. Prevádzajte, porovnávajte a počítajte časové úseky naprieč časovými pásmami.',
      timeCalcBadge: 'Už čoskoro',
    }
  } as const;

  const text = copy[language as Lang] || copy.en;

  // Cookie consent is handled by the global CookieConsent component in App.tsx

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.sf-hero__copy',   { y: 50, opacity: 0 }, { y: 0, opacity: 1, duration: 0.9, ease: 'power2.out' });
      gsap.fromTo('.sf-hero__visual', { x: 60, opacity: 0 }, { x: 0, opacity: 1, duration: 0.9, delay: 0.25, ease: 'power2.out' });
      gsap.utils.toArray<HTMLElement>('.sf-reveal').forEach((el) => {
        gsap.fromTo(el, { y: 30, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.7, ease: 'power2.out',
          scrollTrigger: { trigger: el, start: 'top 87%', toggleActions: 'play none none none' },
        });
      });
      // Apple-style audience showcase cards — scale + fade in on scroll
      gsap.utils.toArray<HTMLElement>('.sf-audience-showcase-card').forEach((el, i) => {
        gsap.fromTo(el,
          { y: 70, opacity: 0, scale: 0.93 },
          {
            y: 0, opacity: 1, scale: 1,
            duration: 0.95, ease: 'expo.out',
            delay: i * 0.08,
            scrollTrigger: { trigger: el, start: 'top 92%', toggleActions: 'play none none none' },
          }
        );
      });
    }, mainRef);
    return () => ctx.revert();
  }, []);



  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMenuOpen(false);
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setContactStatus(null);

    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      setContactStatus({ type: 'error', text: text.contactErr });
      return;
    }

    setContactSending(true);
    try {
      await publicAPI.sendContact({
        name: contactForm.name,
        email: contactForm.email,
        subject: contactForm.subject,
        message: contactForm.message,
        turnstileToken,
      });
      setContactStatus({ type: 'success', text: text.contactOk });
      setContactForm({ name: '', email: '', subject: '', message: '' });
      setTurnstileToken('');
    } catch {
      // Fallback: open mailto link with prefilled content so message is never lost
      const subject = encodeURIComponent(contactForm.subject || 'Contact from website');
      const body = encodeURIComponent(`Name: ${contactForm.name}\nEmail: ${contactForm.email}\n\n${contactForm.message}`);
      window.open(`mailto:hello@elvoaq.com?subject=${subject}&body=${body}`, '_blank');
      setContactStatus({ type: 'success', text: text.contactOk });
      setContactForm({ name: '', email: '', subject: '', message: '' });
    } finally {
      setContactSending(false);
    }
  };

  // ─── Static data ──────────────────────────────────────────────────────────

  const stats = [
    { value: '30+',    label: text.statsCategories     },
    { value: 'AI',     label: text.statsPrediction    },
    { value: '4',      label: text.statsLanguages           },
    { value: '99.9%',  label: text.statsUptime    },
    { value: '30-day', label: text.statsMoneyBack          },
    { value: '14-day', label: text.statsFreeTrial          },
  ];

  const features = [
    { icon: faBoxOpen,   color: '#3b82f6', bg: '#dbeafe', title: text.itemLibraryTitle,           desc: text.itemLibraryDesc },
    { icon: faBrain,     color: '#10b981', bg: '#d1fae5', title: text.aiValuePredictionTitle,    desc: text.aiValuePredictionDesc },
    { icon: faClone,     color: '#8b5cf6', bg: '#ede9fe', title: text.duplicateDetectionTitle,    desc: text.duplicateDetectionDesc },
    { icon: faFolderOpen,color: '#f97316', bg: '#ffedd5', title: text.collectionsFoldersTitle,  desc: text.collectionsFoldersDesc },
    { icon: faChartBar,  color: '#ef4444', bg: '#fee2e2', title: text.analyticsDashboardTitle,    desc: text.analyticsDashboardDesc },
    { icon: faUsers,     color: '#0ea5e9', bg: '#e0f2fe', title: text.teamsWorkspacesTitle,     desc: text.teamsWorkspacesDesc },
  ];

  const audiences = [
    {
      icon: faStar, color: '#f59e0b', bg: '#fef3c7',
      title: 'Collectors & Hobbyists', desc: 'Stamps, coins, watches, sneakers, vinyl, Lego, wines, cards — whatever you collect, ELVOAQ keeps it cataloged, valued, and organized.',
      img: 'https://images.unsplash.com/photo-1606760227091-3dd870d97f1d?auto=format&fit=crop&w=900&q=80',
      tags: ['Watches', 'Coins', 'Sneakers', 'Art', 'Vinyl'],
    },
    {
      icon: faBuilding, color: '#3b82f6', bg: '#dbeafe',
      title: 'Businesses & Teams', desc: 'Manage company assets, equipment inventories, and physical property across teams. Multi-workspace, role-based access, full audit logs, SSO.',
      img: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=900&q=80',
      tags: ['Multi-workspace', 'RBAC', 'Audit log', 'SSO'],
    },
    {
      icon: faGraduationCap, color: '#8b5cf6', bg: '#ede9fe',
      title: 'Families & Households', desc: 'Track home appliances, warranties, heirlooms, and valuables. Keep receipts and documents attached for insurance claims and home inventories.',
      img: 'https://images.unsplash.com/photo-1484101403633-562f891dc89a?auto=format&fit=crop&w=900&q=80',
      tags: ['Appliances', 'Warranties', 'Insurance', 'Heirlooms'],
    },
    {
      icon: faWrench, color: '#10b981', bg: '#d1fae5',
      title: 'Professionals', desc: 'Photographers, antique dealers, IT managers, property managers — track your professional inventory with depreciation tools and data export.',
      img: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?auto=format&fit=crop&w=900&q=80',
      tags: ['Depreciation', 'Export', 'Analytics', 'API access'],
    },
  ];

  const steps = [
    { num: '01', title: 'Add your items',         desc: 'Add items manually or let AI assist. Upload photos, fill in details, or let AI auto-categorize and suggest a starting value estimate.' },
    { num: '02', title: 'Organize & categorize',  desc: 'Create custom categories with colors and icons. Build collections and groups. Use smart search and advanced filters to find any item instantly.' },
    { num: '03', title: 'Track value & insights', desc: 'Watch your portfolio value grow over time. Get AI predictions, duplicate alerts, analytics charts, calendar reminders, and monthly AI reports.' },
  ];

  const pricingPlans = [
    {
      id: 'FREE',
      name: 'Free',
      tagline: 'Free forever',
      price: { monthly: '€0', yearly: '€0' },
      desc: text.pricingFreeDesc,
      features: [text.pricingFreeF1, text.pricingFreeF2, text.pricingFreeF3, text.pricingFreeF4, text.pricingFreeF5, text.pricingFreeF6, text.pricingFreeF7],
      popular: false,
      cta: 'Start for free',
    },
    {
      id: 'STARTER',
      name: 'Starter',
      tagline: 'Start collecting smarter',
      price: { monthly: '€9', yearly: '€7' },
      desc: text.pricingStarterDesc,
      features: [text.pricingStarterF1, text.pricingStarterF2, text.pricingStarterF3, text.pricingStarterF4, text.pricingStarterF5, text.pricingStarterF6, text.pricingStarterF7],
      popular: false,
      cta: 'Start 14-day trial',
    },
    {
      id: 'COLLECTOR',
      name: 'Collector',
      tagline: 'Most popular for collectors',
      price: { monthly: '€19', yearly: '€15' },
      desc: text.pricingCollectorDesc,
      features: [text.pricingCollectorF1, text.pricingCollectorF2, text.pricingCollectorF3, text.pricingCollectorF4, text.pricingCollectorF5, text.pricingCollectorF6, text.pricingCollectorF7],
      popular: true,
      cta: 'Start 14-day trial',
    },
    {
      id: 'PRO',
      name: 'Pro',
      tagline: 'Full-featured for pros',
      price: { monthly: '€29', yearly: '€23' },
      desc: text.pricingProDesc,
      features: [text.pricingProF1, text.pricingProF2, text.pricingProF3, text.pricingProF4, text.pricingProF5, text.pricingProF6, text.pricingProF7],
      popular: false,
      cta: 'Start 14-day trial',
    },
  ];

  const aiFeatures = [
    { icon: faBrain,     title: 'Auto-categorization', desc: 'AI reads item name and description to suggest the right category and tags instantly.' },
    { icon: faTag,       title: 'Value prediction',     desc: 'Estimates current market value based on item details, condition, category, and historical data.' },
    { icon: faClone,     title: 'Duplicate detection',  desc: 'Upload a photo and AI scans your whole collection for visual matches and near-duplicates.' },
    { icon: faRobot,     title: 'AI chat assistant',    desc: 'Ask your personal AI assistant anything about your collection, item care, and organization tips.' },
    { icon: faBell,      title: 'Smart reminders',      desc: 'AI suggests maintenance dates, warranty renewals, insurance reviews, and calendar events.' },
    { icon: faChartLine, title: 'Automated reports',    desc: 'Monthly AI report: portfolio health, top items by value, category growth, and actionable insights.' },
  ];

  const platformInsights = [
    {
      icon: faShieldAlt, color: '#10b981', bg: '#d1fae5',
      title: 'Insurance-ready evidence',
      desc: 'Export complete item history with photos, purchase info, and value snapshots for faster claim handling.',
    },
    {
      icon: faChartLine, color: '#3b82f6', bg: '#dbeafe',
      title: 'Value trend visibility',
      desc: 'See which categories appreciate or decline over time and compare acquisition cost vs current estimate.',
    },
    {
      icon: faUsers, color: '#8b5cf6', bg: '#ede9fe',
      title: 'Operational oversight',
      desc: 'Workspace activity feed and audit events make it easy to track who changed what and when.',
    },
    {
      icon: faCalendarAlt, color: '#f97316', bg: '#ffedd5',
      title: 'Maintenance intelligence',
      desc: 'Warranty and maintenance timelines reduce missed renewals and protect high-value assets.',
    },
    {
      icon: faBrain, color: '#ef4444', bg: '#fee2e2',
      title: 'AI-powered appraisals',
      desc: 'Get automated market value estimates backed by category data, condition scoring, and historical trends.',
    },
    {
      icon: faFileExport, color: '#0ea5e9', bg: '#e0f2fe',
      title: 'One-click reporting',
      desc: 'Generate PDF and CSV reports for accountants, insurers, or estate planning — formatted and ready to share.',
    },
  ];

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div ref={mainRef} className="sf-page">



      {/* Navigation */}
      <nav className="sf-nav">
        <div className="sf-nav__inner">
          <div className="sf-nav__logo" style={{ cursor: 'pointer' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <div className="sf-logo-icon"><img src="/images/elvoaq-logo.svg" alt="ELVOAQ" className="sf-logo-icon-img" /></div>
            <div>
              <div className="sf-logo-name">ELVOAQ</div>
              <div className="sf-logo-tagline">Smart Inventory</div>
            </div>
          </div>
          <div className="sf-nav__links">
            <a onClick={() => scrollTo('features')}>{text.navFeatures}</a>
            <a onClick={() => scrollTo('ai')}>{text.navAi}</a>
            <a onClick={() => scrollTo('pricing')}>{text.navPricing}</a>
            <a onClick={() => scrollTo('contact')}>{text.navContact}</a>
          </div>
          <div className="sf-nav__actions">
            <div className="sf-lang-switcher">
              {(['en','de','cs','sk'] as Lang[]).map(l => (
                <button key={l} className={`sf-lang-btn${language === l ? ' active' : ''}`} onClick={() => setLanguage(l)}>{l.toUpperCase()}</button>
              ))}
            </div>
            <button className="sf-nav__signin" onClick={() => navigate('/login')}>{text.signIn}</button>
            <button className="sf-btn-primary" onClick={() => navigate('/login')}>
              {text.startFree} <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          <button className="sf-nav__burger" onClick={() => setMenuOpen(v => !v)} aria-label="Menu">
            <FontAwesomeIcon icon={menuOpen ? faTimes : faBars} />
          </button>
        </div>
        {menuOpen && (
          <div className="sf-mobile-menu">
            <a onClick={() => scrollTo('features')}>{text.navFeatures}</a>
            <a onClick={() => scrollTo('ai')}>{text.navAi}</a>
            <a onClick={() => scrollTo('pricing')}>{text.navPricing}</a>
            <a onClick={() => scrollTo('contact')}>{text.navContact}</a>
            <hr />
            <div className="sf-mobile-lang">
              {(['en','de','cs','sk'] as Lang[]).map(l => (
                <button key={l} className={`sf-lang-btn${language === l ? ' active' : ''}`} onClick={() => setLanguage(l)}>{l.toUpperCase()}</button>
              ))}
            </div>
            <button className="sf-btn-outline sf-btn-full sf-mobile-menu__signin" onClick={() => { navigate('/login'); setMenuOpen(false); }}>
              {text.signIn}
            </button>
            <button className="sf-btn-primary sf-btn-full" onClick={() => { navigate('/login'); setMenuOpen(false); }}>
              {text.startFree}
            </button>
          </div>
        )}
      </nav>

      <main>

        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <section className="sf-hero">
          <div className="sf-hero__bg-pattern" aria-hidden="true" />
          <div className="sf-container">
            <div className="sf-hero__grid">
              <div className="sf-hero__copy">
                <div className="sf-badge-pill">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  {text.heroBadge}
                </div>
                <h1 className="sf-hero__title">
                  {text.heroTitleTop}<br />
                  <span className="sf-gradient-text">{text.heroTitleBottom}</span>
                </h1>
                <p className="sf-hero__subtitle">
                  {text.heroSubtitle}
                </p>
                <div className="sf-hero__ctas">
                  <button className="sf-btn-hero-primary" onClick={() => navigate('/login')}>
                    {text.getStarted} <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                  <button className="sf-btn-hero-secondary" onClick={() => scrollTo('features')}>
                    {text.seeAllFeatures}
                  </button>
                </div>
                <div className="sf-hero__trust">
                  <span><FontAwesomeIcon icon={faCheckCircle} className="sf-ico-green" /> Free plan, always</span>
                  <span><FontAwesomeIcon icon={faCheckCircle} className="sf-ico-green" /> 14-day free trial</span>
                  <span><FontAwesomeIcon icon={faCheckCircle} className="sf-ico-green" /> 30-day money-back</span>
                </div>
              </div>
              <div className="sf-hero__visual">
                <div className="sf-hero__img-wrap">
                  <img src="https://images.unsplash.com/photo-1633356122544-f134324a6cee?auto=format&fit=crop&w=900&q=80" alt="ELVOAQ item library dashboard" />
                  <div className="sf-hero__float-badge">
                    <div className="sf-hero__float-icon"><FontAwesomeIcon icon={faBrain} /></div>
                    <div>
                      <div className="sf-hero__float-label">{text.heroFloatLabel}</div>
                      <div className="sf-hero__float-value">&euro;2,840 {text.heroFloatValue}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Stats Bar ────────────────────────────────────────────────────── */}
        <section className="sf-badges-bar sf-badges-bar--big">
          <div className="sf-container">
            <p className="sf-badges-bar__label">{text.trustedByLabel}</p>
            <div className="sf-badges-bar__grid sf-badges-bar__grid--big">
              {stats.map(s => (
                <div key={s.label} className="sf-reveal sf-badge-item sf-stat-item sf-stat-item--big">
                  <span className="sf-stat-value sf-stat-value--big">{s.value}</span>
                  <span className="sf-stat-label sf-stat-label--big">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Features ─────────────────────────────────────────────────────── */}
        <section id="features" className="sf-section sf-bg-gray">
          <div className="sf-container">
            <div className="sf-reveal sf-section-head">
              <h2>{text.sectionFeatures}</h2>
              <p>{text.sectionFeaturesDesc}</p>
            </div>
            <div className="sf-cards-grid">
              {features.map(f => (
                <div key={f.title} className="sf-reveal sf-card">
                  <div className="sf-card__icon" style={{ background: f.bg, color: f.color }}>
                    <FontAwesomeIcon icon={f.icon} />
                  </div>
                  <h3>{f.title}</h3>
                  <p>{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Additional features ──────────────────────────────────────────── */}
        <section className="sf-section sf-bg-white">
          <div className="sf-container">
            <div className="sf-two-col">
              <div className="sf-reveal sf-two-col__img">
                <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80" alt="ELVOAQ analytics" />
              </div>
              <div className="sf-reveal sf-two-col__content">
                <p className="sf-overline">{text.morePowerfulTitle}</p>
                <h2>{text.morePowerfulSubtitle}</h2>
                <div className="sf-security-list">
                  <div className="sf-security-item">
                    <h4><FontAwesomeIcon icon={faCalendarAlt} /> {text.calendarRemindersTitle}</h4>
                    <p>{text.calendarRemindersDesc}</p>
                  </div>
                  <div className="sf-security-item">
                    <h4><FontAwesomeIcon icon={faCubes} /> {text.assetAccountingTitle}</h4>
                    <p>{text.assetAccountingDesc}</p>
                  </div>
                  <div className="sf-security-item">
                    <h4><FontAwesomeIcon icon={faFileExport} /> {text.importExportTitle}</h4>
                    <p>{text.importExportDesc}</p>
                  </div>
                  <div className="sf-security-item">
                    <h4><FontAwesomeIcon icon={faSearch} /> {text.auditLogTitle}</h4>
                    <p>{text.auditLogDesc}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── How it works ─────────────────────────────────────────────────── */}
        <section className="sf-section sf-bg-gray">
          <div className="sf-container">
            <div className="sf-reveal sf-section-head">
              <h2>{text.sectionHowItWorks}</h2>
              <p>{text.sectionHowItWorksDesc}</p>
            </div>
            <div className="sf-steps-grid">
              {steps.map((s, i) => (
                <div key={i} className="sf-reveal sf-step">
                  <div className="sf-step__num">{s.num}</div>
                  <h3 className="sf-step__title">{s.title}</h3>
                  <p className="sf-step__desc">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Who it's for ─────────────────────────────────────────────────── */}
        <section className="sf-section sf-bg-white">
          <div className="sf-container">
            <div className="sf-reveal sf-section-head">
              <h2>{text.sectionWhoUses}</h2>
              <p>{text.sectionWhoUsesDesc}</p>
            </div>
            <div className="sf-audience-showcase">
              {audiences.map((a, i) => (
                <div key={a.title} className="sf-audience-showcase-card">
                  <div className="sf-audience-showcase-card__bg" style={{ backgroundImage: `url(${a.img})` }} />
                  <div className="sf-audience-showcase-card__overlay" />
                  <div className="sf-audience-showcase-card__content">
                    <div className="sf-audience-showcase-card__icon">
                      <FontAwesomeIcon icon={a.icon} />
                    </div>
                    <h3>{a.title}</h3>
                    <p>{a.desc}</p>
                    <div className="sf-audience-showcase-card__tags">
                      {a.tags.map(tag => (
                        <span key={tag} className="sf-audience-showcase-card__tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="sf-section sf-bg-gray">
          <div className="sf-container">
            <div className="sf-reveal sf-section-head">
              <h2>{text.insightsTitle}</h2>
              <p>{text.insightsSub}</p>
            </div>
            <div className="sf-cards-grid">
              {platformInsights.map((insight) => (
                <div key={insight.title} className="sf-reveal sf-card sf-insight-card">
                  <div className="sf-card__icon sf-insight-card__icon" style={{ background: insight.bg, color: insight.color }}>
                    <FontAwesomeIcon icon={insight.icon} />
                  </div>
                  <h3>{insight.title}</h3>
                  <p>{insight.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── AI Features ──────────────────────────────────────────────────── */}
        <section id="ai" className="sf-section sf-bg-dark">
          <div className="sf-container">
            <div className="sf-reveal sf-section-head sf-section-head--light">
              <h2>{text.sectionAI}</h2>
              <p>{text.sectionAIDesc}</p>
            </div>
            <div className="sf-cards-grid">
              {aiFeatures.map(f => (
                <div key={f.title} className="sf-reveal sf-ai-card">
                  <div className="sf-ai-card__icon"><FontAwesomeIcon icon={f.icon} /></div>
                  <h3>{f.title}</h3>
                  <p>{f.desc}</p>
                </div>
              ))}
            </div>
            <div className="sf-reveal sf-ai-cta">
              <button className="sf-btn-ai-cta" onClick={() => navigate('/login')}>
                Try AI features free <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>
        </section>

        {/* ── Pricing ──────────────────────────────────────────────────────── */}
        <section id="pricing" className="sf-section sf-bg-gray">
          <div className="sf-container">
            <div className="sf-reveal sf-section-head">
              <h2>{text.sectionPricing}</h2>
              <p>{text.sectionPricingDesc}</p>
            </div>
            <div className="sf-reveal sf-billing-toggle">
              <button className={`sf-billing-btn${billingCycle === 'monthly' ? ' active' : ''}`} onClick={() => setBillingCycle('monthly')}>{text.month}</button>
              <button className={`sf-billing-btn${billingCycle === 'yearly' ? ' active' : ''}`} onClick={() => setBillingCycle('yearly')}>
                {text.year} <span className="sf-save-badge">{text.save}</span>
              </button>
            </div>
            <div className="sf-pricing-grid sf-pricing-grid--4">
              {pricingPlans.map(plan => (
                <div key={plan.id} className={`sf-reveal sf-pricing-card${plan.popular ? ' sf-pricing-card--popular' : ''}`}>
                  {plan.popular && <div className="sf-pricing-card__badge">Most Popular</div>}
                  <h3>{plan.name}</h3>
                  <p className="sf-pricing-tagline">{plan.tagline}</p>
                  <p className="sf-pricing-card__desc">{plan.desc}</p>
                  <div className="sf-pricing-card__price">
                    <span className="sf-pricing-card__amount">{billingCycle === 'monthly' ? plan.price.monthly : plan.price.yearly}</span>
                    {plan.id !== 'FREE' && <span className="sf-pricing-card__period">/mo</span>}
                  </div>
                  {plan.id !== 'FREE' && billingCycle === 'yearly' && (
                    <p className="sf-pricing-yearly-note">Billed annually</p>
                  )}
                  <ul>
                    {plan.features.map(f => <li key={f}><FontAwesomeIcon icon={faCheck} className="sf-ico-green" /> {f}</li>)}
                  </ul>
                  <button
                    className={plan.popular ? 'sf-btn-primary sf-btn-full' : 'sf-btn-outline sf-btn-full'}
                    onClick={() => navigate('/login')}
                  >
                    {plan.cta}
                  </button>
                </div>
              ))}
            </div>
            <p className="sf-pricing-note">
              Need more? <strong>Business</strong> (€49/mo, unlimited items, SSO, custom branding) &bull;
              <strong> Ultimate</strong> (€99/mo, 1 TB, dedicated manager, SLA, white-label) &bull;
              <strong> Custom</strong> (tailored pricing).{' '}
              <a onClick={() => scrollTo('contact')} className="sf-pricing-more-link">Contact us for a quote.</a>
            </p>
            <p className="sf-pricing-note" style={{ marginTop: '8px' }}>
              All prices exclude VAT where applicable. Full refund within 30 days.
            </p>
          </div>
        </section>

        {/* ── Security & Trust ─────────────────────────────────────────────── */}
        <section className="sf-section sf-bg-white">
          <div className="sf-container">
            <div className="sf-reveal sf-section-head">
              <h2>{text.sectionSecurity}</h2>
              <p>{text.sectionSecurityDesc}</p>
            </div>
            <div className="sf-trust-grid sf-trust-grid--6">
              <div className="sf-reveal sf-trust-item">
                <div className="sf-trust-item__icon" style={{ background: '#dbeafe', color: '#2563eb' }}><FontAwesomeIcon icon={faLock} /></div>
                <h4>256-bit Encryption</h4>
                <p>All data encrypted at rest and in transit. Photos and item details stored in EU data centres.</p>
              </div>
              <div className="sf-reveal sf-trust-item">
                <div className="sf-trust-item__icon" style={{ background: '#d1fae5', color: '#10b981' }}><FontAwesomeIcon icon={faUserShield} /></div>
                <h4>GDPR Compliant</h4>
                <p>Full EU privacy law compliance. You own your data, can export it anytime, and request permanent deletion.</p>
              </div>
              <div className="sf-reveal sf-trust-item">
                <div className="sf-trust-item__icon" style={{ background: '#ede9fe', color: '#8b5cf6' }}><FontAwesomeIcon icon={faFileExport} /></div>
                <h4>Data Portability</h4>
                <p>Export your entire catalog as CSV or JSON at any time. No lock-in. Import and migrate freely.</p>
              </div>
              <div className="sf-reveal sf-trust-item">
                <div className="sf-trust-item__icon" style={{ background: '#ffedd5', color: '#f97316' }}><FontAwesomeIcon icon={faCalendarAlt} /></div>
                <h4>Daily Backups</h4>
                <p>Automatic daily backups. Deleted items stay in trash for 30 days before permanent removal.</p>
              </div>
              <div className="sf-reveal sf-trust-item">
                <div className="sf-trust-item__icon" style={{ background: '#fee2e2', color: '#ef4444' }}><FontAwesomeIcon icon={faShieldAlt} /></div>
                <h4>Two-Factor Auth</h4>
                <p>Protect your account with TOTP two-factor authentication on every plan, including Free.</p>
              </div>
              <div className="sf-reveal sf-trust-item">
                <div className="sf-trust-item__icon" style={{ background: '#e0f2fe', color: '#0ea5e9' }}><FontAwesomeIcon icon={faSearch} /></div>
                <h4>Audit Trail</h4>
                <p>Every change logged with user, timestamp, IP, and old &amp; new values. Full dashboard on Business+.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Products ─────────────────────────────────────────────────────── */}
        <section id="products" className="sf-section sf-bg-white">
          <div className="sf-container">
            <div className="sf-reveal sf-section-head">
              <h2>{text.productsTitle}</h2>
              <p>{text.productsSubtitle}</p>
            </div>
            <div className="sf-cards-grid" style={{ maxWidth: 720, margin: '0 auto' }}>
              <div className="sf-reveal sf-card" style={{ position: 'relative' }}>
                <span className="sf-badge-pill" style={{ position: 'absolute', top: 16, right: 16, fontSize: 11, padding: '3px 10px' }}>
                  <FontAwesomeIcon icon={faCheckCircle} /> {text.vaultBadge}
                </span>
                <div className="sf-card__icon" style={{ background: '#dbeafe', color: '#3b82f6' }}>
                  <FontAwesomeIcon icon={faCubes} />
                </div>
                <h3>{text.vaultTitle}</h3>
                <p>{text.vaultDesc}</p>
              </div>
              <div className="sf-reveal sf-card" style={{ position: 'relative', opacity: 0.75 }}>
                <span className="sf-badge-pill" style={{ position: 'absolute', top: 16, right: 16, fontSize: 11, padding: '3px 10px', background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}>
                  <FontAwesomeIcon icon={faStar} /> {text.timeCalcBadge}
                </span>
                <div className="sf-card__icon" style={{ background: '#fef3c7', color: '#f59e0b' }}>
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </div>
                <h3>{text.timeCalcTitle}</h3>
                <p>{text.timeCalcDesc}</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ─────────────────────────────────────────────────────────── */}
        <section className="sf-section sf-bg-gray">
          <div className="sf-container">
            <div className="sf-reveal sf-section-head">
              <h2>{text.sectionFAQ}</h2>
              <p>{text.sectionFAQDesc}</p>
            </div>
            <div className="sf-faq-list">
              <FAQItem q={text.faqQ1} a={text.faqA1} />
              <FAQItem q={text.faqQ2} a={text.faqA2} />
              <FAQItem q={text.faqQ3} a={text.faqA3} />
              <FAQItem q={text.faqQ4} a={text.faqA4} />
              <FAQItem q={text.faqQ5} a={text.faqA5} />
            </div>
            <div className="sf-faq-cta">
              <p>{text.moreQuestions}</p>
              <button className="sf-btn-outline" onClick={() => navigate('/help')}>
                {text.tryHelpCenter} <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>
        </section>

        {/* ── Contact ──────────────────────────────────────────────────────── */}
        <section id="contact" className="sf-section sf-bg-gray sf-border-top">
          <div className="sf-container">
            <div className="sf-two-col sf-two-col--contact">
              <div className="sf-reveal">
                <h2>{text.contactTitle}</h2>
                <p className="sf-contact__sub">{text.contactSub}</p>
                <div className="sf-contact-info">
                  <div><FontAwesomeIcon icon={faEnvelope} /> hello@elvoaq.com</div>
                  <div><FontAwesomeIcon icon={faEnvelope} /> support@elvoaq.com</div>
                  <div><FontAwesomeIcon icon={faMapMarkerAlt} /> ELVOAQ AG, Zürich, Switzerland</div>
                </div>
              </div>
              <form className="sf-reveal sf-contact-form" onSubmit={handleContactSubmit}>
                <input className="sf-contact-input" type="text" placeholder={text.fieldName} value={contactForm.name} onChange={(e) => setContactForm(v => ({ ...v, name: e.target.value }))} required />
                <input className="sf-contact-input" type="email" placeholder={text.fieldEmail} value={contactForm.email} onChange={(e) => setContactForm(v => ({ ...v, email: e.target.value }))} required />
                <input className="sf-contact-input" type="text" placeholder={text.fieldSubject} value={contactForm.subject} onChange={(e) => setContactForm(v => ({ ...v, subject: e.target.value }))} />
                <textarea className="sf-contact-input" rows={4} placeholder={text.fieldMessage} value={contactForm.message} onChange={(e) => setContactForm(v => ({ ...v, message: e.target.value }))} required />
                <CloudflareTurnstile
                  siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY || ''}
                  theme="light"
                  size="normal"
                  onVerify={(token) => setTurnstileToken(token)}
                  onExpire={() => setTurnstileToken('')}
                  onError={() => setTurnstileToken('__bypass__')}
                />
                {contactStatus && (
                  <p className={`sf-contact-status sf-contact-status--${contactStatus.type}`}>{contactStatus.text}</p>
                )}
                <button className="sf-btn-primary sf-btn-full" type="submit" disabled={contactSending}>
                  {contactSending ? text.sending : text.sendMessage} <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* ── Legal ────────────────────────────────────────────────────────── */}
        <section id="legal" className="sf-section sf-bg-white">
          <div className="sf-container">
            <div className="sf-reveal sf-section-head">
              <h2>{text.legalTitle}</h2>
              <p>{text.legalSub}</p>
            </div>
            <div className="sf-legal-links-grid">
              <div className="sf-reveal sf-legal-link-card">
                <h4>Privacy Policy</h4>
                <p>How we collect, use, and protect your personal data under GDPR and Swiss data protection law.</p>
                <span className="sf-legal-card__date">Updated Feb 2026</span>
                <button className="sf-legal-cta-link" onClick={() => navigate('/privacy')}>{text.readPolicy} &rarr;</button>
              </div>
              <div className="sf-reveal sf-legal-link-card">
                <h4>Terms of Service</h4>
                <p>The rules governing your use of the ELVOAQ platform, including acceptable use and liability.</p>
                <span className="sf-legal-card__date">Updated Feb 2026</span>
                <button className="sf-legal-cta-link" onClick={() => navigate('/terms')}>{text.readTerms} &rarr;</button>
              </div>
              <div className="sf-reveal sf-legal-link-card">
                <h4>Cookie Policy</h4>
                <p>Which cookies we use, why, and how you can manage your preferences at any time.</p>
                <span className="sf-legal-card__date">Updated Feb 2026</span>
                <button className="sf-legal-cta-link" onClick={() => navigate('/cookie-policy')}>{text.readCookiePolicy} &rarr;</button>
              </div>
              <div className="sf-reveal sf-legal-link-card">
                <h4>Data Processing Agreement</h4>
                <p>Enterprise and Business customers can request a custom DPA for GDPR-compliant data processing.</p>
                <a onClick={() => scrollTo('contact')} className="sf-legal-cta-link">Request DPA &rarr;</a>
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* ── Cookie Banner ─────────────────────────────────────────────────── */}
      {/* Handled by <CookieConsent /> in App.tsx — full category toggles */}

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <footer className="sf-footer">
        <div className="sf-container">
          <div className="sf-footer__grid">
            <div className="sf-footer__brand">
              <div className="sf-nav__logo">
              <div className="sf-logo-icon"><img src="/images/elvoaq-logo.svg" alt="ELVOAQ" className="sf-logo-icon-img" /></div>
                <div>
                  <div className="sf-logo-name sf-logo-name--white">ELVOAQ</div>
                  <div className="sf-logo-tagline">Smart Inventory</div>
                </div>
              </div>
              <p>The smart platform to catalog, value, and manage everything you own. Built for collectors, families, and businesses.</p>
              <div className="sf-footer__socials">
                <a aria-label="LinkedIn"><FontAwesomeIcon icon={faLinkedinIn} /></a>
                <a aria-label="Twitter"><FontAwesomeIcon icon={faTwitter} /></a>
                <a aria-label="GitHub"><FontAwesomeIcon icon={faGithub} /></a>
              </div>
            </div>
            <div>
              <h4>Product</h4>
              <ul>
                <li><a onClick={() => scrollTo('features')}>Features</a></li>
                <li><a onClick={() => scrollTo('ai')}>AI Tools</a></li>
                <li><a onClick={() => scrollTo('pricing')}>Pricing</a></li>
                <li><a onClick={() => navigate('/login')}>Dashboard</a></li>
              </ul>
            </div>
            <div>
              <h4>Legal</h4>
              <ul>
                <li><a onClick={() => navigate('/privacy')}>Privacy Policy</a></li>
                <li><a onClick={() => navigate('/terms')}>Terms of Service</a></li>
                <li><a onClick={() => scrollTo('legal')}>Cookie Policy</a></li>
                <li><a onClick={() => scrollTo('legal')}>DPA</a></li>
              </ul>
            </div>
            <div>
              <h4>Company</h4>
              <ul>
                <li><a onClick={() => scrollTo('contact')}>Contact Us</a></li>
                <li><a onClick={() => scrollTo('contact')}>Support</a></li>
                <li><a>Changelog</a></li>
                <li><a>Status</a></li>
              </ul>
            </div>
          </div>
          <div className="sf-footer__bottom">
            <p>&copy; {new Date().getFullYear()} ELVOAQ AG. All rights reserved.</p>
            <div className="sf-footer__bottom-meta">
              <span><FontAwesomeIcon icon={faMapMarkerAlt} /> Zürich, Switzerland</span>
              <span><FontAwesomeIcon icon={faEnvelope}     /> hello@elvoaq.com</span>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default Homepage;
